import * as Yup from "yup";
import { motion } from "framer-motion";
import {
    VisalLogin,
    VisalLoginVariables,
    VisalLogin_login_user,
} from "_graphql/visal/mutations/__generated__/VisalLogin";
import { useNavigate, useSearch } from "react-location";

import { FC } from "react";
import { LOGIN } from "_graphql/visal/mutations/auth";
// import { LOGIN } from "_graphql/visal/mutations/auth";
import { LocationGenerics } from "router/location";
import { TextInput } from "components/core";
import { getAppDetails } from "router/filter";
import { setAuth } from "_graphql/cache/auth";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { Sparkles } from "lucide-react";

const LoginPage: FC = () => {
    const search = useSearch<LocationGenerics>();
    const navigate = useNavigate();
    const [login, { loading }] = useMutation<VisalLogin, VisalLoginVariables>(
        LOGIN,
    );
    const { name, logo } = getAppDetails();
    const { handleSubmit, ...form } = useFormik({
        initialValues: {
            username: "",
            password: "",
        },
        validationSchema: Yup.object().shape({
            username: Yup.string().email().required(),
            password: Yup.string().required(),
        }),
        onSubmit: async (values) => {
            await login({
                variables: {
                    input: {
                        password: values.password,
                        username: values.username?.toLowerCase(),
                    },
                },
            }).then(({ data }) => {
                if (data?.login) {
                    if (data.login?.user?.first_time_signin) {
                        return navigate({
                            replace: true,
                            to: `/cpanel-reset-password?id=${data?.login?.user?.employee?.employee_id}&redirect=${search?.redirect ?? "/"}`,
                        });
                    } else {
                        setAuth({
                            user: data?.login?.user as VisalLogin_login_user,
                            token: data.login.access_token ?? "",
                            agentToken: data.login.agent_token ?? "",
                        });
                        return navigate({
                            replace: true,
                            to: search?.redirect ?? "/",
                        });
                    }
                }
            });
        },
    });

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full h-full p-5 flex flex-col justify-center items-center">
            <div className="w-full max-w-lg space-y-6 ">
                {/* Logo */}
                <div className="flex justify-center items-center relative text-[#125bed] w-fit mx-auto gap-2">
                    <motion.img
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.1 }}
                        src={logo}
                        alt="Visal Re"
                        className="w-32 mx-auto"
                    />
                    <span className="text-2xl font-bold">Lite</span>
                </div>

                {/* AI Badge */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="flex items-center justify-center gap-2 text-sm font-medium text-[#2e5cb8] mb-2">
                    <Sparkles className="w-4 h-4" />
                    <span className=" tracking-wide">
                        AI Powered Reinsurance Dashboard
                    </span>
                </motion.div>

                {/* Title */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-center space-y-2">
                    <h1 className="font-bold text-3xl text-slate-800">
                        Welcome back
                    </h1>
                </motion.div>

                <p className=" text-sm text-tertiary text-center w-full">
                    Enter your credentials below
                </p>
                {search?.from === "reset_success" && (
                    <div className="bg-teal-100 px-2 rounded-md py-1 mt-3 w-full">
                        <p className="text-sm text-teal-600 text-center w-full">
                            Password Changed SuccessFully. Login to access the{" "}
                            {name} Reinsurance Dashboard
                        </p>
                    </div>
                )}
            </div>

            <div className="mt-2 w-full max-w-lg ">
                <div className="mt-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <TextInput
                                id="username"
                                label="Email address"
                                type="email"
                                placeholder="e.g. ebaidoo79@gmail.com"
                                {...form}
                            />
                        </div>

                        <div className="space-y-1">
                            <TextInput
                                id="password"
                                label="Password"
                                type="password"
                                placeholder="e.g.  **************"
                                {...form}
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    name="remember-me"
                                    type="checkbox"
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <label
                                    htmlFor="remember-me"
                                    className="ml-2 block text-sm text-tertiary">
                                    Remember me
                                </label>
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#2e5cb8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                                {loading ? "Signing in..." : "Sign in"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </motion.div>
    );
};

export default LoginPage;
